import React from 'react';
import { Link } from 'react-router-dom';
import { BRAND, NAV, PRODUCTS } from '../data/mock';
import { Mail, MapPin, Phone, ArrowUpRight, Clock, Instagram } from 'lucide-react';
import Logo from './Logo';
import GuaranteesStrip from './GuaranteesStrip';
import { openCookieSettings } from './CookieBanner';

export default function Footer() {
  return (
    <>
    <GuaranteesStrip compact />
    <footer className="relative bg-[#314F2B] border-t border-[#E7E9E4]/8 overflow-hidden">
      <div className="absolute inset-0 grain" />

      {/* Pre-footer CTA strip — cinematic install photo with frosted glass
          content card. Gives a strong visual transition out of the page
          before falling into the quiet dark-green footer below. */}
      <div className="relative border-b border-[#E7E9E4]/8 overflow-hidden">
        {/* Background photo: real Guardian Supreme install, slow Ken-Burns
            drift, layered with a dark gradient so the headline stays
            crisp on the left and the photo bleeds open on the right. */}
        <div className="absolute inset-0">
          <img
            src="/hero/hero-stone-path.jpg"
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover hero-ken-burns"
            style={{ objectPosition: 'center 55%' }}
          />
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(to right, rgba(15,26,20,0.96) 0%, rgba(22,42,30,0.92) 35%, rgba(22,42,30,0.78) 55%, rgba(22,42,30,0.30) 80%, rgba(22,42,30,0.10) 100%)'
          }} />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#314F2B] to-transparent" />
          <div className="absolute inset-0 grain opacity-40" />
        </div>

        <div className="relative mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-12 gap-6 items-center" style={{ maxWidth: '1700px' }}>
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#A3C66F] mb-4 font-bold">— Ready when you are</div>
            <h3
              className="font-serif-editorial text-3xl md:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-[#E7E9E4] max-w-[600px]"
              style={{ textShadow: '0 4px 32px rgba(0,0,0,0.85), 0 2px 10px rgba(0,0,0,0.75)' }}
            >
              Transform your <span className="italic font-light text-[#A3C66F]" style={{ textShadow: '0 4px 28px rgba(0,0,0,0.8)' }}>outdoor space</span> today.
            </h3>
            <p
              className="mt-5 max-w-[520px] text-sm md:text-[15px] text-[#E7E9E4] leading-relaxed"
              style={{ textShadow: '0 2px 12px rgba(0,0,0,0.70)' }}
            >
              Free, no-obligation home survey · Detailed written quote within 48 hours · 24-month groundworks guarantee.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 flex md:justify-end">
            {/* Frosted CTA card */}
            <div
              className="rounded-2xl p-5 md:p-6 w-full md:w-auto"
              style={{
                backdropFilter: 'blur(18px) saturate(125%)',
                WebkitBackdropFilter: 'blur(18px) saturate(125%)',
                background: 'linear-gradient(135deg, rgba(231,233,228,0.18), rgba(22,42,30,0.32))',
                border: '1px solid rgba(231,233,228,0.22)',
                boxShadow: '0 30px 80px -20px rgba(0,0,0,0.55)',
              }}
            >
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#E7E9E4]/85 mb-3 font-bold">Speak to the team</div>
              <Link to="/contact" data-cursor="hover" className="group inline-flex items-center gap-3 bg-[#6A8F3E] hover:brightness-110 text-[#162A1E] px-7 py-4 rounded-full transition-all">
                <span className="text-[11px] tracking-[0.25em] uppercase font-bold">Request a Quote</span>
                <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main footer grid */}
      <div className="relative mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-8 md:pb-12" style={{ maxWidth: '1700px' }}>
        {/* Darker gradient veil behind the upper third of the footer —
            lifts contrast for the logo + section headings without
            committing to a flat dark band. Fades back into the footer's
            mid-green by the lower half of the grid. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[70%] pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(15,26,20,0.85) 0%, rgba(15,26,20,0.55) 35%, rgba(15,26,20,0.15) 75%, rgba(15,26,20,0) 100%)',
          }}
        />
        <div className="relative md:grid md:grid-cols-12 md:gap-8">
          {/* Logo + tagline — full width on mobile, narrow column on desktop */}
          <div className="md:col-span-3 mb-8 md:mb-0">
            <div className="mb-5 md:mb-6">
              <Logo size="xl" />
            </div>
            <p className="text-sm text-[#E7E9E4]/90 leading-relaxed max-w-[280px]">
              Premium artificial lawns that look beautiful, feel natural and are built to last. Transform your outdoor space today.
            </p>
          </div>

          {/* Mobile: 2-col grid of Company + Services side-by-side (instead of two long stacked lists).
              Desktop: Company gets 2 columns, Services gets 3. */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 mb-8 md:mb-0 md:contents">
            {/* Company */}
            <div className="md:col-span-2">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#A3C66F] mb-4 md:mb-5 font-bold">Company</div>
              <ul className="space-y-2.5 md:space-y-3">
                {NAV.filter(n => !n.dropdown).map(n => (
                  <li key={n.href}>
                    <Link to={n.href} data-cursor="hover" className="text-sm text-[#E7E9E4] hover:text-[#6A8F3E] transition-colors link-underline">
                      {n.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/installation" data-cursor="hover" className="text-sm text-[#E7E9E4] hover:text-[#6A8F3E] transition-colors link-underline">
                    Our Process
                  </Link>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div className="md:col-span-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#6A8F3E] mb-4 md:mb-5 font-semibold">Services</div>
              <ul className="space-y-2.5 md:space-y-3">
                {PRODUCTS.slice(0, 6).map(p => (
                  <li key={p.slug}>
                    <Link to="/products" data-cursor="hover" className="text-sm text-[#E7E9E4] hover:text-[#6A8F3E] transition-colors link-underline">
                      {p.name} ({p.pile})
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Service areas (internal links — local SEO) */}
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#6A8F3E] mt-6 mb-3 font-semibold">Service Areas</div>
              <ul className="space-y-2 md:space-y-2.5">
                <li><Link to="/artificial-lawns-london" data-cursor="hover" className="text-sm text-[#E7E9E4] hover:text-[#6A8F3E] transition-colors link-underline">Artificial Lawns London</Link></li>
                <li><Link to="/artificial-lawns-kent"   data-cursor="hover" className="text-sm text-[#E7E9E4] hover:text-[#6A8F3E] transition-colors link-underline">Artificial Lawns Kent</Link></li>
                <li><Link to="/artificial-lawns-surrey" data-cursor="hover" className="text-sm text-[#E7E9E4] hover:text-[#6A8F3E] transition-colors link-underline">Artificial Lawns Surrey</Link></li>
                <li><Link to="/artificial-lawns-essex"  data-cursor="hover" className="text-sm text-[#E7E9E4] hover:text-[#6A8F3E] transition-colors link-underline">Artificial Lawns Essex</Link></li>
              </ul>
            </div>
          </div>

          {/* We're Open + Contact — horizontal pill grid on mobile so it doesn't stack tall */}
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#A3C66F] mb-4 md:mb-5 font-bold">We're Open</div>
            <div className="flex items-start gap-3 mb-5 md:mb-6">
              <Clock size={14} className="mt-0.5 text-[#6A8F3E] shrink-0" strokeWidth={1.5} />
              <div className="text-sm text-[#E7E9E4]">
                Mon — Fri<span className="text-[#E7E9E4]/65 mx-2 md:mx-3">·</span>07:30 — 17:30
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:gap-4">
              <div className="flex items-start gap-3 text-sm text-[#E7E9E4]">
                <MapPin size={14} className="mt-0.5 text-[#6A8F3E] shrink-0" strokeWidth={1.5} />
                <span>{BRAND.area}<br /><span className="text-[#E7E9E4]/65">Greater London · Kent · Surrey · Essex</span></span>
              </div>
              <div className="flex items-start gap-3 text-sm text-[#E7E9E4]">
                <Mail size={14} className="mt-0.5 text-[#6A8F3E] shrink-0" strokeWidth={1.5} />
                <a href={`mailto:${BRAND.email}`} data-cursor="hover" className="link-underline break-all">{BRAND.email}</a>
              </div>
              <div className="flex items-start gap-3 text-sm text-[#E7E9E4]">
                <Phone size={14} className="mt-0.5 text-[#6A8F3E] shrink-0" strokeWidth={1.5} />
                <a href={`tel:${BRAND.phoneTel}`} data-cursor="hover" className="link-underline">{BRAND.phone}</a>
              </div>
              <a
                href={process.env.REACT_APP_INSTAGRAM_URL || '#'}
                target="_blank"
                rel="noopener noreferrer"
                data-cursor="hover"
                data-testid="footer-instagram"
                className="group flex items-start gap-3 text-sm text-[#E7E9E4] hover:text-[#A3C66F] transition-colors"
              >
                <Instagram size={14} className="mt-0.5 text-[#6A8F3E] shrink-0 group-hover:text-[#A3C66F] transition-colors" strokeWidth={1.5} />
                <span className="link-underline">Follow @guardianartificiallawns</span>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 md:mt-12 pt-6 md:pt-8 border-t border-[#E7E9E4]/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[11px] tracking-[0.18em] uppercase text-[#E7E9E4]/90">
          <div>© {new Date().getFullYear()} Guardian Artificial Lawns. All rights reserved.</div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/privacy" data-cursor="hover" data-testid="footer-privacy-link" className="hover:text-[#6A8F3E] transition-colors">Privacy</Link>
            <Link to="/terms" data-cursor="hover" data-testid="footer-terms-link" className="hover:text-[#6A8F3E] transition-colors">Terms</Link>
            <button
              type="button"
              onClick={openCookieSettings}
              data-cursor="hover"
              data-testid="footer-cookie-settings"
              className="hover:text-[#6A8F3E] transition-colors uppercase tracking-[0.18em] text-[11px]"
            >
              Cookie Settings
            </button>
            <span className="text-[#E7E9E4]/55">Part of Guardian Builders Ltd</span>
          </div>
        </div>
      </div>

      {/* Giant grass-texture brand wordmark — blends from Forest above into Obsidian below */}
      <div className="relative overflow-hidden bg-[#162A1E]" data-testid="footer-wordmark-wrap">
        {/* Top fade: tight blend so there isn't excess green space above the wordmark */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-12 md:h-16 bg-gradient-to-b from-[#314F2B] via-[#264322] to-transparent pointer-events-none z-10" />
        <picture>
          <source srcSet="/brand/guardian-grass.webp" type="image/webp" />
          <img
            src="/brand/guardian-grass.png"
            alt="Guardian"
            loading="lazy"
            decoding="async"
            className="relative block w-full h-auto select-none pointer-events-none px-2 sm:px-0"
            style={{
              objectFit: 'contain',
              // `lighten` blend mode: image's dark background gets replaced
              // by the Obsidian container colour, so only the bright grass
              // letters stay visible — no visible image edge / seam at all.
              mixBlendMode: 'lighten',
              minHeight: '90px',
            }}
            data-testid="footer-grass-wordmark"
          />
        </picture>
        {/* Bottom vignette grounds the wordmark */}
        <div aria-hidden className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#0A1610] to-transparent pointer-events-none z-10" />
      </div>
    </footer>
    </>
  );
}
